import React from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import { ArrowLeft, Clock, User, Tag } from 'lucide-react'
import { getDocumentById } from '@/config/documents'
import DemoInterfaceGuide from '@content/docs/demo-interface-guide.mdx'

// Map document IDs to their corresponding MDX components
const documentComponents: Record<string, React.ComponentType> = {
  'demo-interface-guide': DemoInterfaceGuide,
}

export const DocumentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  
  if (!id) {
    return <Navigate to="/document" replace />
  }

  const documentInfo = getDocumentById(id)
  const DocumentComponent = documentComponents[id]

  if (!documentInfo || !DocumentComponent) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">文档未找到</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">您要查找的文档不存在或已被移除。</p>
          <Link 
            to="/document"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowLeft size={16} />
            返回文档列表
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link 
          to="/document"
          className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6 text-sm font-medium"
        >
          <ArrowLeft size={16} />
          返回文档列表
        </Link>
        
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-block px-3 py-1 text-sm font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-full">
              {documentInfo.category}
            </span>
            {documentInfo.difficulty && (
              <span className="inline-block px-3 py-1 text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full">
                {documentInfo.difficulty === 'beginner' ? '初级' : 
                 documentInfo.difficulty === 'intermediate' ? '中级' : '高级'}
              </span>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {documentInfo.title}
          </h1>
          
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            {documentInfo.description}
          </p>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
            {documentInfo.author && (
              <div className="flex items-center gap-1">
                <User size={14} />
                <span>{documentInfo.author}</span>
              </div>
            )}
            {documentInfo.readTime && (
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>{documentInfo.readTime}</span>
              </div>
            )}
            <div>
              最后更新: {documentInfo.updatedAt}
            </div>
          </div>
          
          {documentInfo.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {documentInfo.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md"
                >
                  <Tag size={10} />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Document Content */}
      <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-gray-900 dark:prose-code:text-gray-200 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300">
        <DocumentComponent />
      </div>
    </div>
  )
}
